// noinspection DuplicatedCode
//
// Halaman kiosk absensi wajah - satu-satunya halaman di app ini yang render
// TANPA Sidebar (route-nya sengaja diletakkan di luar <AuthCheck/>, lihat
// App.js). Tablet di sini TIDAK PERNAH login sbg karyawan - auth-nya device
// token (X-Device-Token), bukan JWT. State sengaja LOKAL (bukan Redux/store
// app utama) karena halaman ini benar2 terisolasi dari sesi karyawan yang
// mungkin sedang login di tab/perangkat lain.
//
// Alur: pairing (masukkan kode sekali) -> scan wajah terus-menerus (hands-free,
// bukan tombol tap - karyawan pelabuhan sering tangannya penuh/kotor) ->
// konfirmasi nama -> pilih Masuk/Pulang -> submit -> auto
// kembali ke scan. Layar scan tanpa karyawan yang berhasil dikenali selama
// SLEEP_AFTER_MS -> mode tidur: layar gelap + jam, KAMERA DIMATIKAN (lampu
// kamera padam, hemat baterai) dan pengenalan wajah berhenti; bangun dengan
// menyentuh layar.
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import Webcam from "react-webcam";
import {
  loadKioskModels,
  detectFaceDescriptorFast,
  findBestMatch,
  meanDescriptor,
  assessFaceQuality,
  runFrameLoop,
} from "../../../lib/faceRecognition";
import { speak } from "../../../lib/speech";
import "./KioskAbsensiPage.css";

const STORAGE_KEY = "kioskDeviceToken";
// Metode lama yg ringan (2026-10-06): model tiny, scan berjalan terus (frame
// berikutnya langsung setelah frame sebelumnya selesai, jeda minimal
// SCAN_GAP_MS), cukup 2 frame berturut-turut orang yg sama lalu descriptor
// dirata-rata utk konfirmasi.
const STABLE_TICKS_REQUIRED = 2;
const SCAN_GAP_MS = 120;
const MATCH_THRESHOLD = 0.5;
const MATCH_MARGIN = 0.06; // selisih minimal dgn karyawan terdekat kedua (anti tertukar)
const UNKNOWN_HINT_TICKS = 12; // ~3 dtk wajah layak tapi tak dikenali -> tampilkan petunjuk
const DATASET_REFRESH_MS = 15 * 60 * 1000;
const RESULT_DISPLAY_MS = 3000;

// Mode tidur & batas tunggu layar konfirmasi.
const SLEEP_AFTER_MS = 100000; // 100 dtk di layar scan tanpa ada yang dikenali -> tidur
const CONFIRM_IDLE_MS = 20000; // layar konfirmasi/pilih absen tak disentuh -> kembali scan
const HARI = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus",
  "September", "Oktober", "November", "Desember"];

const apiUrl = (path) => `${process.env.REACT_APP_API_URL}kiosk/${path}`;

const KioskAbsensiPage = () => {
  const [deviceToken, setDeviceToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [phase, setPhase] = useState("init"); // init|pairing|loading|scanning|detected|confirm-type|submitting|result
  const [codeInput, setCodeInput] = useState("");
  const [pairError, setPairError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [matchedPerson, setMatchedPerson] = useState(null);
  const [resultInfo, setResultInfo] = useState(null); // {ok, message}
  const [hint, setHint] = useState(""); // petunjuk saat scan (dekatkan wajah, dll.)
  const [clock, setClock] = useState(() => moment());

  const webcamRef = useRef(null);
  const datasetRef = useRef([]);
  const stableRef = useRef({ nik: null, count: 0, descs: [] });
  const unknownRef = useRef(0);
  const tokenRef = useRef(deviceToken);
  tokenRef.current = deviceToken;

  const authHeaders = useCallback(() => ({ "X-Device-Token": tokenRef.current }), []);

  const clearPairing = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setDeviceToken("");
    setDeviceInfo(null);
    setPhase("pairing");
  }, []);

  const fetchDataset = useCallback(async () => {
    const res = await axios.get(apiUrl("enrolled-faces"), { headers: authHeaders() });
    datasetRef.current = res.data?.data || [];
  }, [authHeaders]);

  const bootstrap = useCallback(async () => {
    setPhase("loading");
    setLoadError("");
    try {
      const info = await axios.get(apiUrl("device-info"), { headers: authHeaders() });
      setDeviceInfo(info.data?.data || null);
      await loadKioskModels();
      await fetchDataset();
      setPhase("scanning");
    } catch (e) {
      if (e?.response?.status === 401) {
        clearPairing();
        return;
      }
      setLoadError(e?.response?.data?.message || e.message || "Gagal memuat data kiosk");
      setPhase("loading");
    }
  }, [authHeaders, clearPairing, fetchDataset]);

  useEffect(() => {
    if (deviceToken) {
      bootstrap();
    } else {
      setPhase("pairing");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceToken]);

  // Refresh dataset diam-diam secara berkala supaya karyawan yg baru
  // dienroll muncul tanpa perlu reload manual tablet-nya.
  useEffect(() => {
    if (phase === "init" || phase === "pairing" || phase === "loading") return;
    const id = setInterval(() => {
      fetchDataset().catch(() => {});
    }, DATASET_REFRESH_MS);
    return () => clearInterval(id);
  }, [phase, fetchDataset]);

  const handlePair = async (e) => {
    e.preventDefault();
    if (!codeInput.trim()) return;
    setPairError("");
    try {
      const res = await axios.post(
        apiUrl("pair"),
        new URLSearchParams({ DeviceCode: codeInput.trim() })
      );
      const token = res.data?.data?.DeviceToken;
      if (!token) throw new Error("Respons pairing tidak valid");
      localStorage.setItem(STORAGE_KEY, token);
      setCodeInput("");
      setDeviceToken(token);
    } catch (e) {
      setPairError(e?.response?.data?.message || e.message || "Gagal memasangkan perangkat");
    }
  };

  // ── Loop scan: jalan hanya saat phase === "scanning" ─────────────────
  const resetStable = useCallback(() => {
    stableRef.current = { nik: null, count: 0, descs: [] };
  }, []);

  const runDetectionTick = useCallback(async () => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState !== 4) return;
    let result;
    try {
      result = await detectFaceDescriptorFast(video);
    } catch (e) {
      return;
    }
    if (!result) {
      resetStable();
      unknownRef.current = 0;
      setHint("");
      return;
    }

    const quality = assessFaceQuality(result, video, { maxYaw: 0.45, minFaceWidth: 0.15, minScore: 0.5 });
    if (quality) {
      resetStable();
      setHint(quality.hint);
      return;
    }

    const descriptor = Array.from(result.descriptor);
    const match = findBestMatch(descriptor, datasetRef.current, MATCH_THRESHOLD, MATCH_MARGIN);
    if (!match || match.ambiguous) {
      resetStable();
      unknownRef.current += 1;
      setHint(
        match?.ambiguous
          ? "Tahan wajah sebentar…"
          : unknownRef.current >= UNKNOWN_HINT_TICKS
          ? "Wajah belum dikenali — hubungi HRD bila belum terdaftar"
          : "Tahan wajah sebentar…"
      );
      return;
    }
    unknownRef.current = 0;
    setHint("Tahan wajah sebentar…");

    const st = stableRef.current;
    if (st.nik === match.Nik) {
      st.count += 1;
      st.descs.push(descriptor);
    } else {
      stableRef.current = { nik: match.Nik, count: 1, descs: [descriptor] };
    }
    if (stableRef.current.count >= STABLE_TICKS_REQUIRED) {
      // Konfirmasi akhir dgn descriptor rata-rata beberapa frame: harus tetap
      // orang yg sama & tetap lolos threshold + margin.
      const confirmed = findBestMatch(
        meanDescriptor(stableRef.current.descs),
        datasetRef.current,
        MATCH_THRESHOLD,
        MATCH_MARGIN
      );
      resetStable();
      if (!confirmed || confirmed.ambiguous || confirmed.Nik !== match.Nik) return;
      setHint("");
      setMatchedPerson(confirmed);
      setPhase("detected");
    }
  }, [resetStable]);

  useEffect(() => {
    if (phase !== "scanning") return;
    resetStable();
    unknownRef.current = 0;
    setHint("");
    let last = 0;
    const stopLoop = runFrameLoop(async () => {
      if (Date.now() - last < SCAN_GAP_MS) return;
      last = Date.now();
      await runDetectionTick();
    });
    // Tidur bila layar scan bertahan SLEEP_AFTER_MS tanpa ada yang dikenali
    // (dikenali = pindah phase, timer ini otomatis batal).
    const sleepId = setTimeout(() => setPhase("sleep"), SLEEP_AFTER_MS);
    return () => {
      stopLoop();
      clearTimeout(sleepId);
    };
  }, [phase, runDetectionTick, resetStable]);

  // ── Layar konfirmasi tak disentuh -> kembali scan (lalu tidur bila sepi) ─
  useEffect(() => {
    if (phase !== "detected" && phase !== "confirm-type") return;
    const id = setTimeout(() => {
      setMatchedPerson(null);
      setPhase("scanning");
    }, CONFIRM_IDLE_MS);
    return () => clearTimeout(id);
  }, [phase]);

  // ── Mode tidur: kamera DIMATIKAN (Webcam tidak dirender -> stream & lampu
  //    kamera berhenti); bangun hanya dgn sentuhan / tombol. ────────────────
  useEffect(() => {
    if (phase !== "sleep") return;
    const wake = () => setPhase("scanning");
    window.addEventListener("pointerdown", wake);
    window.addEventListener("keydown", wake);
    return () => {
      window.removeEventListener("pointerdown", wake);
      window.removeEventListener("keydown", wake);
    };
  }, [phase]);

  // Jam di layar tidur.
  useEffect(() => {
    if (phase !== "sleep") return;
    setClock(moment());
    const id = setInterval(() => setClock(moment()), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const backToScanning = () => {
    setMatchedPerson(null);
    setResultInfo(null);
    setPhase("scanning");
  };

  const handleSubmitScan = async (inOutMode) => {
    setPhase("submitting");
    try {
      const res = await axios.post(
        apiUrl("submit-scan"),
        new URLSearchParams({
          Nik: matchedPerson.Nik,
          InOutMode: String(inOutMode),
          MatchDistance: String(matchedPerson.distance || 0),
          // Jam dinding LOKAL tablet ini (bukan jam server) - kiosk bisa
          // dipasang di lokasi tugas beda zona waktu (WITA/WIT), jadi jangan
          // dipaksa WIB. Backend simpan apa adanya kalau formatnya valid,
          // kalau tidak (kiosk lama/klien iseng) jatuh balik ke jam server.
          ClientScanTime: moment().format("YYYY-MM-DD HH:mm:ss.SSS"),
        }),
        { headers: authHeaders() }
      );
      const isDuplicate = res.data?.status === "Duplicate";
      const jenisAbsen = inOutMode === 1 ? "Masuk" : "Pulang";
      setResultInfo({
        ok: !isDuplicate,
        message: isDuplicate
          ? res.data?.message || "Sudah tercatat absen beberapa saat lalu"
          : `Absen ${jenisAbsen} tercatat — ${matchedPerson.NamaKaryawan} — ${res.data?.data?.ScanDate || ""}`,
      });
      // Voice-over hanya utk absen yang BENAR-BENAR berhasil (bukan duplikat/
      // gagal) - permintaan eksplisit user, dan supaya bunyi "berhasil" tak
      // pernah keliru diucapkan saat sebenarnya ditolak.
      if (!isDuplicate) {
        speak(`${matchedPerson.NamaKaryawan}, absen ${jenisAbsen.toLowerCase()} berhasil dicatat`);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        clearPairing();
        return;
      }
      setResultInfo({
        ok: false,
        message: e?.response?.data?.message || e.message || "Gagal menyimpan absen",
      });
    } finally {
      setPhase("result");
      setTimeout(backToScanning, RESULT_DISPLAY_MS);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────

  // Header tetap di atas, tampil di SEMUA tahap - "ABSENSI RKS" besar +
  // (kalau ada) nama device kecil di bawahnya.
  const TopBar = () => (
    <div className="kiosk-topbar">
      <div className="kiosk-topbar-title">ABSENSI RKS</div>
      {deviceInfo?.Nama && (
        <div className="kiosk-device-badge">
          <span className="kiosk-live-dot" />
          {deviceInfo.Nama}
        </div>
      )}
    </div>
  );

  if (phase === "pairing") {
    return (
      <div className="kiosk-root">
        <div className="kiosk-grid" />
        <TopBar />
        <div className="kiosk-content">
          <p className="kiosk-subtitle">Masukkan kode perangkat yang diberikan admin HRD</p>
          <div className="kiosk-glass">
            <form onSubmit={handlePair} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <input
                type="text"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="• • • • • • • •"
                autoFocus
                className="kiosk-input"
              />
              <button type="submit" className="kiosk-btn kiosk-btn-primary">
                Pasangkan Perangkat
              </button>
            </form>
            {pairError && <p className="kiosk-error">{pairError}</p>}
          </div>
        </div>
      </div>
    );
  }

  if (phase === "init" || phase === "loading") {
    return (
      <div className="kiosk-root">
        <div className="kiosk-grid" />
        <TopBar />
        <div className="kiosk-content" style={{ alignItems: "center" }}>
          <div className="kiosk-spinner" />
          <p className="kiosk-status">Menyiapkan sistem pengenalan wajah…</p>
          {loadError && (
            <>
              <p className="kiosk-error">{loadError}</p>
              <button onClick={bootstrap} className="kiosk-btn kiosk-btn-primary kiosk-retry-btn">
                Coba Lagi
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const ringClass =
    phase === "detected" || phase === "confirm-type"
      ? "kiosk-ring is-success"
      : phase === "result" && resultInfo && !resultInfo.ok
      ? "kiosk-ring is-alert"
      : "kiosk-ring";

  // scanning | detected | confirm-type | submitting | result | sleep -
  // webcam tetap dipasang terus supaya tak perlu init ulang kamera tiap phase
  // (dan supaya mode tidur bisa mendeteksi gerakan utk bangun).
  return (
    <div className="kiosk-root">
      {phase === "sleep" && (
        <div className="kiosk-sleep" onClick={() => setPhase("scanning")}>
          <div className="kiosk-sleep-time">{clock.format("HH:mm")}</div>
          <div className="kiosk-sleep-date">
            {HARI[clock.day()]}, {clock.format("DD")} {BULAN[clock.month()]} {clock.format("YYYY")}
          </div>
          <div className="kiosk-sleep-hint">Sentuh layar untuk absen</div>
        </div>
      )}
      <div className="kiosk-grid" />
      <TopBar />
      <div className="kiosk-content">
        <div className="kiosk-frame-wrap">
          <div className={ringClass} />
          <div className="kiosk-frame">
            {/* Tidak dirender saat tidur: react-webcam menghentikan stream
                kamera saat unmount -> lampu kamera padam, hemat baterai. */}
            {phase !== "sleep" && (
              <Webcam
                ref={webcamRef}
                audio={false}
                mirrored
                screenshotFormat="image/jpeg"
                videoConstraints={{ width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" }}
              />
            )}
            {phase === "scanning" && <div className="kiosk-scanline" />}
            <span className="kiosk-bracket tl" />
            <span className="kiosk-bracket tr" />
            <span className="kiosk-bracket bl" />
            <span className="kiosk-bracket br" />
          </div>
        </div>

        {phase === "scanning" && (
          <p className={`kiosk-status${hint ? " kiosk-status-hint" : ""}`}>
            {hint || "Arahkan wajah ke kamera"}
            <span className="kiosk-dot" />
            <span className="kiosk-dot" />
            <span className="kiosk-dot" />
          </p>
        )}

        {phase === "detected" && matchedPerson && (
          <>
            <p className="kiosk-name">{matchedPerson.NamaKaryawan}</p>
            <p className="kiosk-name-caption">Konfirmasi identitas</p>
            <div className="kiosk-btn-row">
              <button onClick={() => setPhase("confirm-type")} className="kiosk-btn kiosk-btn-success">
                Ya, Ini Saya
              </button>
              <button onClick={backToScanning} className="kiosk-btn kiosk-btn-danger">
                Bukan Saya
              </button>
            </div>
          </>
        )}

        {phase === "confirm-type" && matchedPerson && (
          <>
            <p className="kiosk-name">{matchedPerson.NamaKaryawan}</p>
            <p className="kiosk-name-caption">Pilih jenis absen</p>
            <div className="kiosk-btn-row">
              <button onClick={() => handleSubmitScan(1)} className="kiosk-btn kiosk-btn-primary">
                Absen Masuk
              </button>
              <button
                onClick={() => handleSubmitScan(2)}
                className="kiosk-btn"
                style={{ background: "linear-gradient(135deg, #7c3aed, #4f46e5)", boxShadow: "0 8px 24px rgba(124,58,237,0.35)" }}
              >
                Absen Pulang
              </button>
            </div>
            <button onClick={backToScanning} className="kiosk-btn kiosk-btn-ghost" style={{ marginTop: 14 }}>
              Batal
            </button>
          </>
        )}

        {phase === "submitting" && (
          <>
            <div className="kiosk-spinner" style={{ marginTop: 8 }} />
            <p className="kiosk-status">Menyimpan absen…</p>
          </>
        )}

        {phase === "result" && resultInfo && (
          <>
            <div className={`kiosk-result-icon ${resultInfo.ok ? "ok" : "warn"}`}>
              {resultInfo.ok ? (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#34d399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L14.71 3.86a2 2 0 0 0-3.42 0Z"
                    stroke="#fbbf24"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <p className={`kiosk-result-text ${resultInfo.ok ? "ok" : "warn"}`}>{resultInfo.message}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default KioskAbsensiPage;
